import { useFeatureFlag } from '@newfront-insurance/admin-ui';
import { isNotEmpty } from '@newfront-insurance/core';
import { cn } from '@newfront-insurance/core-ui/v2';
import { useCallback, useEffect, useRef } from 'react';
import { useAttachments } from './attachments-provider';
import { useChatInputContext, useDragHandlersRef } from './chat-input-context';
import { SlashCommandMenu, useSlashCommandMenu } from './slash-command-menu';
import { useSlashCommands } from '../../../../hooks/use-slash-commands';
import { getPlainTextFromElement, saveCursorPosition, restoreCursorPosition } from '../../../../lib/contenteditable-utils';
import type { SlashCommand } from '../../../../queries/chats';
import { useChatOverlayContext } from '../../contexts/chat-overlay-context';
interface ChatInputTextareaProps {
  placeholder?: string;
  className?: string;
}

/**
 * Textarea component for chat input.
 * Handles text input, auto-height adjustment, and keyboard shortcuts.
 * Uses context to access input state and submit handler.
 * When slash commands are enabled, uses contenteditable for rich text rendering.
 */
export function ChatInputTextarea({
  placeholder = 'How can I help you today?',
  className
}: ChatInputTextareaProps) {
  const {
    input,
    setInput,
    editorRef,
    handleSubmit,
    status,
    selectedSlashCommand,
    setSelectedSlashCommand
  } = useChatInputContext();
  const {
    accountUuid,
    userUuid,
    additionalContext
  } = useChatOverlayContext();
  const {
    attachments
  } = useAttachments();
  const handlersRef = useDragHandlersRef();
  const {
    handlePaste
  } = handlersRef.current;
  const hasUploadingFiles = attachments.some(attachment => attachment.isUploading);
  const successfulAttachmentUuids = attachments.filter(attachment_0 => attachment_0.fileUuid && !attachment_0.uploadError).map(attachment_1 => attachment_1.fileUuid!);
  const contextAttachmentFileUuids = Array.isArray(additionalContext?.attachmentFileUuids) ? additionalContext.attachmentFileUuids.filter((uuid): uuid is string => typeof uuid === 'string') : [];
  const hasUnsyncedAttachmentContext = successfulAttachmentUuids.length !== contextAttachmentFileUuids.length || successfulAttachmentUuids.some(uuid_0 => !contextAttachmentFileUuids.includes(uuid_0));

  // Feature flag for slash commands
  const isSlashCommandEnabled = useFeatureFlag('nova-slash-command', false);

  // Slash command menu state
  const slashCommandMenu = useSlashCommandMenu();
  const {
    commands
  } = useSlashCommands({
    accountUuid: isSlashCommandEnabled ? accountUuid : undefined,
    userUuid: isSlashCommandEnabled ? userUuid : undefined
  });

  // Track if we're updating the content programmatically
  const isUpdatingRef = useRef(false);

  // Track if a slash command was just selected from the menu (to prevent Enter from submitting)
  const justSelectedCommandRef = useRef(false);
  const adjustHeight = useCallback(() => {
    if (editorRef.current) {
      editorRef.current.style.height = 'auto';
      editorRef.current.style.height = `${editorRef.current.scrollHeight + 2}px`;
    }
  }, [editorRef]);
  const resetHeight = useCallback(() => {
    if (editorRef.current) {
      editorRef.current.style.height = 'auto';
      editorRef.current.style.height = '92px';
    }
  }, [editorRef]);

  /**
   * Check if text starts with a command handle followed by a word boundary (space or end of string).
   * This prevents /get_policy from matching /get_policy_info.
   */
  const hasValidCommandBoundary = useCallback((text: string, handle: string): boolean => {
    const handleWithSlash = `/${handle}`;
    if (!text.startsWith(handleWithSlash)) return false;
    const afterHandle = text.slice(handleWithSlash.length);
    return afterHandle === '' || afterHandle.startsWith(' ');
  }, []);

  // Find a matching slash command from the input with valid word boundary
  const findMatchingCommand = useCallback((text_0: string): SlashCommand | null => {
    if (!text_0.startsWith('/')) return null;

    // Extract the command part (everything after / until space or end)
    const match = text_0.match(/^\/([^\s]+)/);
    if (!match) return null;
    const typedHandle = match[1];
    // Find exact match with word boundary validation
    const command = commands.find(cmd => cmd.handle === typedHandle);
    if (command && hasValidCommandBoundary(text_0, command.handle)) {
      return command;
    }
    return null;
  }, [commands, hasValidCommandBoundary]);

  // Render content with highlighted slash command
  const renderContent = useCallback((text_1: string, matchedCommand: SlashCommand | null): string => {
    if (!matchedCommand || !text_1.startsWith(`/${matchedCommand.handle}`)) {
      // No slash command - return escaped text
      return text_1.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/\n/g, '<br>');
    }
    const handle_0 = `/${matchedCommand.handle}`;
    const rest = text_1.slice(handle_0.length);

    // Render handle in fire-400 color, rest as normal text
    const escapedRest = rest.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/\n/g, '<br>');

    // Show instruction placeholder when user hasn't typed beyond the command handle
    const hasExtraText = rest.trim().length > 0;
    const escapedPlaceholder = matchedCommand.instructionPlaceholder?.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
    const placeholderHtml = !hasExtraText && escapedPlaceholder ? `<span class="text-steel-300 pointer-events-none select-none" contenteditable="false" data-instruction-placeholder="true">${escapedPlaceholder}</span>` : '';
    return `<span class="text-fire-400">${handle_0}</span>${escapedRest}${placeholderHtml}`;
  }, []);

  // Update the contenteditable display
  const updateDisplay = useCallback((text_2: string, command_0: SlashCommand | null) => {
    const editor = editorRef.current;
    if (!editor || !(editor instanceof HTMLDivElement)) return;
    isUpdatingRef.current = true;
    const cursorPos = saveCursorPosition(editor);
    editor.innerHTML = renderContent(text_2, command_0) || '<br>';
    restoreCursorPosition(editor, cursorPos);
    isUpdatingRef.current = false;
  }, [editorRef, renderContent]);

  // Handle input changes for contenteditable
  const handleContentEditableInput = useCallback(() => {
    const editor_0 = editorRef.current;
    if (!editor_0 || !(editor_0 instanceof HTMLDivElement) || isUpdatingRef.current) return;
    const newValue = getPlainTextFromElement(editor_0);
    setInput(newValue);
    adjustHeight();

    // Auto-detect typed slash commands and update selection state
    const matchedCommand_0 = findMatchingCommand(newValue);
    const currentCommandStillValid = selectedSlashCommand && hasValidCommandBoundary(newValue, selectedSlashCommand.handle);

    // Handle slash command menu detection - don't reopen if a command is already selected
    if (newValue.startsWith('/') && !matchedCommand_0 && !currentCommandStillValid) {
      if (!slashCommandMenu.isOpen) {
        slashCommandMenu.open(newValue);
      } else {
        slashCommandMenu.updateQuery(newValue);
      }
    } else if (slashCommandMenu.isOpen) {
      slashCommandMenu.close();
    }
    if (matchedCommand_0) {
      // Valid command found - update selection if changed
      if (selectedSlashCommand?.uuid !== matchedCommand_0.uuid) {
        setSelectedSlashCommand(matchedCommand_0);
        slashCommandMenu.close();
      }
      updateDisplay(newValue, matchedCommand_0);
    } else if (currentCommandStillValid) {
      // Keep current selection and display
      updateDisplay(newValue, selectedSlashCommand);
    } else if (selectedSlashCommand) {
      // Clear invalid selection
      setSelectedSlashCommand(null);
      updateDisplay(newValue, null);
    }
  }, [editorRef, setInput, adjustHeight, slashCommandMenu, findMatchingCommand, hasValidCommandBoundary, selectedSlashCommand, setSelectedSlashCommand, updateDisplay]);

  // Handle slash command selection from menu
  const handleSlashCommandSelect = useCallback((command_1: SlashCommand) => {
    const newInput = `/${command_1.handle} `;
    setInput(newInput);
    setSelectedSlashCommand(command_1);
    slashCommandMenu.close();
    justSelectedCommandRef.current = true;

    // Update contenteditable content
    const editor_1 = editorRef.current;
    if (editor_1 instanceof HTMLDivElement) {
      editor_1.innerHTML = renderContent(newInput, command_1);
      // Place cursor right after the command handle + space, before the instruction placeholder
      const range = document.createRange();
      const placeholderEl = editor_1.querySelector('[data-instruction-placeholder]');
      if (placeholderEl) {
        range.setStartBefore(placeholderEl);
        range.collapse(true);
      } else {
        range.selectNodeContents(editor_1);
        range.collapse(false);
      }
      const selection = window.getSelection();
      selection?.removeAllRanges();
      selection?.addRange(range);
    }
    editorRef.current?.focus();
  }, [setInput, setSelectedSlashCommand, slashCommandMenu, editorRef, renderContent]);
  const submitForm = useCallback(() => {
    if (hasUploadingFiles || hasUnsyncedAttachmentContext) {
      return;
    }
    handleSubmit(undefined);
    resetHeight();
    // Clear contenteditable content
    const editor_2 = editorRef.current;
    if (editor_2 instanceof HTMLDivElement) {
      editor_2.innerHTML = '<br>';
    }
  }, [hasUploadingFiles, hasUnsyncedAttachmentContext, handleSubmit, resetHeight, editorRef]);

  // Sync input state to contenteditable when input changes externally
  useEffect(() => {
    const editor_3 = editorRef.current;
    if (!editor_3 || !(editor_3 instanceof HTMLDivElement)) return;
    const currentText = getPlainTextFromElement(editor_3);
    if (currentText !== input) {
      updateDisplay(input, selectedSlashCommand);
    }
  }, [input, editorRef, selectedSlashCommand, updateDisplay]);

  // Initial height adjustment
  useEffect(() => {
    if (editorRef.current) {
      adjustHeight();
    }
  }, [adjustHeight, editorRef]);

  // Handle paste for contenteditable - strip formatting
  const handleContentEditablePaste = useCallback((event: React.ClipboardEvent<HTMLDivElement>) => {
    event.preventDefault();
    const text_3 = event.clipboardData.getData('text/plain');
    document.execCommand('insertText', false, text_3);

    // Also call the external paste handler for file handling
    if (handlePaste) {
      handlePaste(event as unknown as React.ClipboardEvent<HTMLTextAreaElement | HTMLDivElement>);
    }
  }, [handlePaste]);

  // Common styles for the editor
  const editorStyles = cn('max-h-[calc(25dvh)] min-h-[24px] resize-none overflow-y-auto', 'bg-background !text-base transition-colors', 'min-h-[92px] border-none focus-within:border-none hover:focus-within:border-none', 'rounded-[10px] px-3 py-2.5', 'focus:ring-0 focus:outline-none', 'break-words whitespace-pre-wrap', className, {
    'rounded-b-[0px] border-b-0': isNotEmpty(attachments)
  });

  // Handle keydown for both modes
  const handleKeyDown = useCallback((event_0: React.KeyboardEvent<HTMLDivElement | HTMLTextAreaElement>) => {
    // When slash command menu is open, handle navigation keys
    if (isSlashCommandEnabled && slashCommandMenu.isOpen) {
      if (event_0.key === 'Escape') {
        event_0.preventDefault();
        slashCommandMenu.close();
        return;
      }
      if (event_0.key === 'ArrowDown' || event_0.key === 'ArrowUp' || event_0.key === 'Tab' || event_0.key === 'Enter') {
        // Prevent default to stop cursor movement and let SlashCommandMenu handle these
        event_0.preventDefault();
        return;
      }
    }
    if (event_0.key === 'Enter' && !event_0.shiftKey && !event_0.nativeEvent.isComposing) {
      event_0.preventDefault();
      // Don't submit if a slash command was just selected from the menu via Enter
      if (justSelectedCommandRef.current) {
        justSelectedCommandRef.current = false;
        return;
      }
      if (status === 'ready' && !hasUploadingFiles && !hasUnsyncedAttachmentContext) {
        submitForm();
      }
    }
  }, [isSlashCommandEnabled, slashCommandMenu.isOpen, status, hasUploadingFiles, hasUnsyncedAttachmentContext, submitForm]);

  // Use contenteditable when slash commands are enabled
  if (isSlashCommandEnabled) {
    return <div className="relative flex w-full flex-col gap-4">
        <SlashCommandMenu isOpen={slashCommandMenu.isOpen} onClose={slashCommandMenu.close} onSelect={handleSlashCommandSelect} commands={commands} searchQuery={slashCommandMenu.searchQuery} anchorRef={editorRef as React.RefObject<HTMLDivElement>} />
        <div ref={editorRef as React.RefObject<HTMLDivElement>} role="textbox" aria-label={placeholder} tabIndex={0} contentEditable data-testid="chat-input" data-placeholder={placeholder} className={cn(editorStyles, 'empty:before:text-muted-foreground empty:before:content-[attr(data-placeholder)]')} onInput={handleContentEditableInput} onPaste={handleContentEditablePaste} onKeyDown={handleKeyDown} suppressContentEditableWarning />
      </div>;
  }

  // Fallback to regular textarea when slash commands are disabled
  return <div className="relative flex w-full flex-col gap-4">
      <textarea data-testid="chat-input" ref={editorRef as React.RefObject<HTMLTextAreaElement>} placeholder={placeholder} value={input} onChange={e => {
      setInput(e.target.value);
      adjustHeight();
    }} onPaste={handlePaste as React.ClipboardEventHandler<HTMLTextAreaElement>} className={editorStyles} rows={2}
    // eslint-disable-next-line jsx-a11y/no-autofocus
    autoFocus onKeyDown={handleKeyDown} />
    </div>;
}